<template>
  <v-container class="modern-view pb-12">
    <!-- Premium Header -->
    <v-row class="mb-8 align-center px-4 pt-4">
      <div class="d-flex align-center">
        <v-avatar color="primary" size="48" class="mr-4 shadow-sm border-white-sm">
          <v-icon color="white">fas fa-coins</v-icon>
        </v-avatar>
        <div>
          <div class="text-h4 font-weight-black primary--text lh-1">OT <span class="secondary--text">Valorizadas</span></div>
          <div class="caption grey--text font-weight-bold uppercase tracking-widest mt-1">Reporte de ingresos proyectados y facturación por proyecto</div>
        </div>
      </div>
      <v-spacer></v-spacer>
      <div class="d-flex align-center">
        <export-work-orders-money :disabled="loading" :filter="filter" :options="options" :search="search"/>
      </div>
    </v-row>

    <!-- Modern Filters Card -->
    <v-card outlined class="modern-card pa-6 mb-8 shadow-sm">
      <v-row dense>
        <v-col cols="12" md="3">
          <div class="caption font-weight-black grey--text text-uppercase mb-1 ml-1">Creada Desde</div>
          <v-text-field 
            hide-details 
            :disabled="loading" 
            dense 
            v-model='filter.created_after' 
            outlined 
            type="date" 
            class="modern-input"
          />
        </v-col>
        <v-col cols="12" md="3">
          <div class="caption font-weight-black grey--text text-uppercase mb-1 ml-1">Creada Hasta</div>
          <v-text-field 
            hide-details 
            :disabled="loading" 
            dense 
            v-model='filter.created_before' 
            clearable 
            outlined 
            type="date" 
            class="modern-input"
          />
        </v-col>
        <v-col cols="12" md="3">
          <div class="caption font-weight-black grey--text text-uppercase mb-1 ml-1">Entrega Desde</div>
          <v-text-field 
            hide-details 
            :disabled="loading" 
            dense 
            v-model='filter.delivery_after' 
            outlined 
            type="date" 
            class="modern-input"
          />
        </v-col>
        <v-col cols="12" md="3">
          <div class="caption font-weight-black grey--text text-uppercase mb-1 ml-1">Entrega Hasta</div>
          <v-text-field 
            hide-details 
            :disabled="loading" 
            dense 
            v-model='filter.delivery_before' 
            clearable 
            outlined 
            type="date" 
            class="modern-input"
          />
        </v-col>
      </v-row>
      <v-row dense class="mt-4">
        <v-col cols="12" md="3">
          <div class="caption font-weight-black grey--text text-uppercase mb-1 ml-1">Estatus OT</div>
          <v-select
            v-model="filter.status"
            :items="status"
            item-text="text"
            item-value="value"
            outlined
            clearable
            dense
            hide-details
            :disabled="loading"
            class="modern-input"
          />
        </v-col>
        <v-col cols="12" md="3">
          <Can I="readAll" a="SalesNote">
            <div class="caption font-weight-black grey--text text-uppercase mb-1 ml-1">Vendedor</div>
            <v-select
              dense
              v-model="filter.seller"
              :items="sellers"
              item-text="email"
              item-value="id"
              :loading="sellersLoading"
              outlined
              clearable
              :disabled="loading"
              hide-details
              class="modern-input"
            >
              <template v-slot:item="{ item }">
                <v-avatar size="24" color="primary lighten-5" class="mr-3">
                  <span class="primary--text font-weight-bold" style="font-size: 10px;">{{ item.first_name[0] }}</span>
                </v-avatar>
                <div class="d-flex flex-column">
                  <span class="body-2 font-weight-bold">{{ item.first_name }} {{ item.last_name }}</span>
                  <span class="caption grey--text">{{ item.email }}</span>
                </div>
              </template>
            </v-select>
          </Can>
        </v-col>
        <v-col cols="12" md="3">
          <div class="caption font-weight-black grey--text text-uppercase mb-1 ml-1">Tipo de Orden</div>
          <v-select
            dense
            v-model="filter.reposicion"
            :items="reposicionOptions"
            item-text="text"
            item-value="value"
            outlined
            clearable
            placeholder="Todas"
            :disabled="loading"
            hide-details
            class="modern-input"
          />
        </v-col>
        <v-spacer></v-spacer>
        <v-col cols="12" md="auto" class="d-flex align-end pb-1">
          <v-btn color="primary" icon class="grey lighten-4 shadow-xs" @click="fetch" :loading="loading">
            <v-icon small>fas fa-sync</v-icon>
          </v-btn>
        </v-col>
      </v-row>
    </v-card>

    <!-- Main Table Card -->
    <v-card outlined class="modern-card overflow-hidden shadow-sm">
      <v-data-table
        :headers="headers"
        :items="workOrders"
        :options.sync="options"
        :server-items-length="totalWorkOrders"
        :loading="loading"
        class="modern-table"
        @update:options="fetch"
        :footer-props="{
          showFirstLastPage: true,
          itemsPerPageOptions: [10, 20, 50, 100],
        }"
      >
        <!-- Integrated Search Header -->
        <template v-slot:top>
          <v-sheet color="bg-lightgrey" class="px-6 py-4 d-flex align-center border-bottom bg-lightgrey">
            <span class="caption font-weight-black uppercase tracking-widest grey--text">Órdenes de Trabajo Valorizadas ({{ totalWorkOrders }})</span>
            <v-spacer></v-spacer>
            <v-text-field
              v-model="search"
              @keyup="searchTimeOut"
              placeholder="Buscar por OT, proyecto, cliente..."
              prepend-inner-icon="fas fa-search"
              outlined dense hide-details clearable
              :disabled="loading"
              class="modern-search-field"
            ></v-text-field>
          </v-sheet>
        </template>

        <template v-slot:item.id="{ item }">
          <show-work-order :work_order_id="item.id">
            <v-chip x-small label dark color="primary" class="font-weight-black">OT #{{ item.id }}</v-chip>
          </show-work-order>
        </template>

        <template v-slot:item.created_at="{ item }">
          <div class="d-flex flex-column">
            <span class="body-2 font-weight-bold grey--text text--darken-3">{{ item.created_at | moment("DD/MM/YYYY") }}</span>
            <span class="caption grey--text" v-if="item.completed_at">
              Fin: {{ item.completed_at | moment("DD/MM/YYYY") }}
            </span>
          </div>
        </template>

        <template v-slot:item.sales_note__name="{ item }">
          <div class="body-2 font-weight-bold primary--text lh-1-2 py-2">{{ item.sales_note__name }}</div>
          <div class="caption grey--text font-weight-black uppercase">CN #{{ item.sales_note__id }}</div>
        </template>

        <template v-slot:item.sales_note__customer__name="{ item }">
          <div class="caption font-weight-bold grey--text text--darken-2">{{ item.sales_note__customer__name }}</div>
          <div class="caption grey--text d-flex align-center mt-1">
            <v-icon x-small class="mr-1" color="grey lighten-1">fas fa-user-tie</v-icon>
            {{ item.sales_note__customer__seller__first_name }}
          </div>
        </template>

        <template v-slot:item.delivery_date="{ item }">
          <v-chip x-small outlined class="font-weight-bold" :color="getDeliveryColor(item.delivery_date)">
            {{ item.delivery_date | moment("DD/MM/YYYY") }}
          </v-chip>
        </template>

        <template v-slot:item.flags="{ item }">
          <div class="d-flex justify-center">
            <v-tooltip bottom color="error" v-if="item.flags.red">
              <template v-slot:activator="{ on, attrs }">
                <v-icon v-bind="attrs" v-on="on" small color="error">fas fa-exclamation-triangle</v-icon>
              </template>
              <span>Precio debajo del costo</span>
            </v-tooltip>
            <v-tooltip bottom color="warning" v-if="item.flags.yellow">
              <template v-slot:activator="{ on, attrs }">
                <v-icon v-bind="attrs" v-on="on" small color="warning">fas fa-exclamation-triangle</v-icon>
              </template>
              <span>Precio cerca del límite</span>
            </v-tooltip>
          </div>
        </template>

        <template v-slot:item.total="{ item }">
          <div class="body-1 font-weight-black secondary--text tracking-tighter">{{ item.totals.total | moneda }}</div>
        </template>
      </v-data-table>
    </v-card>
  </v-container>
</template>

<script>
import {fetchWorkOrdersMoney} from '@/api/reports'
import CustomerComplete2 from '@/components/customers/CustomerComplete2.vue'
import ExportWorkOrdersMoney from '@/components/reports/ExportWorkOrdersMoney'
import ShowWorkOrder from '@/components/production/ShowWorkOrder.vue'
import moment from 'moment'
import notifications from '@/utils/notifications'

export default {
  name: 'WorkOrdersWithMoney',
  components: { CustomerComplete2, ExportWorkOrdersMoney, ShowWorkOrder },
  data() {
    return {
      loading: false,
      status: [
        { text: 'Vigentes', value: false },
        { text: 'Completadas', value: true },
        { text: 'Todas', value: '' }
      ],
      reposicionOptions: [
        { text: 'Reposición', value: 'true' },
        { text: 'Normal', value: 'false' },
      ],
      filter: {
        created_after: moment().startOf('month').format('YYYY-MM-DD'),
        created_before: '',
        delivery_after: '',
        delivery_before: '',
        status: '',
        seller: '',
        reposicion: ''
      },
      options: {},
      totalWorkOrders: 0,
      search: '',
      workOrders: [],
      sellers: [],
      sellersLoading: false,
      headers: [
        { text: 'OT', align: 'start', value: 'id', class: 'table-header', width: '110px' },
        { text: 'Cronología', align: 'start', value: 'created_at', class: 'table-header', width: '130px' },
        { text: 'Proyecto', align: 'start', value: 'sales_note__name', class: 'table-header' },
        { text: 'Cliente / Vendedor', align: 'start', value: 'sales_note__customer__name', class: 'table-header' },
        { text: 'Entrega', align: 'start', value: 'delivery_date', class: 'table-header', width: '120px' },
        { text: 'Alerta', align: 'center', value: 'flags', class: 'table-header', width: '80px' },
        { text: 'Total Valorizado', align: 'end', value: 'total', class: 'table-header', width: '160px' },
      ],
    }
  },
  methods: {
    async fetch() {
      this.loading = true
      let created_before = ''
      let created_after = ''
      if (this.filter.created_after) created_after = moment(this.filter.created_after).utc().format('YYYY-MM-DD HH:mm')
      if (this.filter.created_before) created_before = moment(this.filter.created_before).utc().endOf('day').format('YYYY-MM-DD HH:mm')

      const { sortBy, sortDesc, page, itemsPerPage } = this.options
      if (!this.$can('readAll', 'SalesNote')) {
        const user = this.$store.getters.getUser
        this.filter.seller = user.id
      }

      const query = `page=${page}&page_size=${itemsPerPage}&ordering=${sortDesc && sortDesc[0] ? sortBy[0] : `-${sortBy ? sortBy[0] : 'created_at'}`}&search=${this.search}&created_after=${created_after}&created_before=${created_before}&delivery_after=${this.filter.delivery_after || ''}&delivery_before=${this.filter.delivery_before || ''}&completed=${this.filter.status}&sales_note__customer__seller=${this.filter.seller || ''}&sales_note__reposicion=${this.filter.reposicion || ''}`
      
      try {
        const data = await fetchWorkOrdersMoney(query)
        this.workOrders = data.results
        this.totalWorkOrders = data.count
      } catch (err) {
        notifications.showError(err)
      } finally {
        this.loading = false
      }
    },
    searchTimeOut() {
      if (this.timer) {
        clearTimeout(this.timer);
      }
      this.timer = setTimeout(() => {
        this.fetch()
      }, 700);
    },
    getDeliveryColor(date) {
      if (!date) return 'grey'
      const diff = moment(date).diff(moment(), 'days')
      if (diff < 0) return 'error'
      if (diff < 3) return 'orange'
      return 'primary'
    }
  },
  async mounted() {
    this.sellersLoading = true
    this.sellers = this.$store.getters.getSellers
    this.sellersLoading = false
  },
  watch: {
    options: {
      handler: 'fetch',
      deep: true
    },
    filter: {
      deep: true,
      handler: 'fetch'
    },
  }
}
</script>

<style scoped>
.modern-view { background-color: #f4f7f9; min-height: 100vh; }

/* Modern Card Styles */
.modern-card { 
  border-radius: 12px !important; 
  border: 1px solid rgba(0,0,0,0.05) !important; 
  background-color: white !important; 
}
.shadow-sm { box-shadow: 0 4px 12px rgba(0,0,0,0.04) !important; }
.shadow-xs { box-shadow: 0 1px 3px rgba(0,0,0,0.05) !important; }

/* Table Styling */
.modern-table >>> thead th {
  background-color: #f8fbfc !important;
  height: 56px !important;
  text-transform: uppercase;
  font-size: 0.75rem !important;
  letter-spacing: 1px;
  font-weight: 900 !important;
  color: #607d8b !important;
}
.modern-table >>> td {
  height: 64px !important;
  border-bottom: 1px solid #f0f3f5 !important;
}

/* Input Styling */
.modern-input >>> .v-input__control >>> .v-input__slot {
  background-color: #f8fbfc !important;
  border-radius: 8px !important;
}
.modern-search-field { max-width: 400px; }
.modern-search-field >>> .v-input__control >>> .v-input__slot {
  background-color: white !important;
  border-radius: 12px !important;
}

/* Utilities */
.border-bottom { border-bottom: 1px solid rgba(0,0,0,0.05) !important; }
.bg-lightgrey { background-color: #f8fbfc; }
.lh-1 { line-height: 1 !important; }
.lh-1-2 { line-height: 1.25 !important; }
.uppercase { text-transform: uppercase; letter-spacing: 0.8px; }
.tracking-widest { letter-spacing: 1.8px; }
.tracking-tighter { letter-spacing: -1.0px; }
.border-white-sm { border: 2px solid white !important; }
</style>